/**
 * ExpenseFormModal — добавление/редактирование расхода.
 * v2: + account_id, object_id, region, новые payment_methods.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createExpense, updateExpense,
  fetchPaymentAccounts, fetchObjects, fetchAccruals,
  uploadExpenseAttachment, deleteExpenseAttachment, expenseAttachmentUrl,
  KIND_LABELS, PAYMENT_LABELS,
  type ExpenseRow, type ExpenseCategoryRow, type ExpensePayload,
  type PaymentMethod, type CategoryKind,
} from "@/api/finance";
import { ApiError } from "@/api/client";
import { Icon } from "@/components/prototype/Icon";


interface Props {
  open: boolean;
  initial?: ExpenseRow | null;
  categories: ExpenseCategoryRow[];
  onClose: () => void;
  onSaved: () => void;
}

function todayIso(): string {
  const d = new Date(Date.now() + 3 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}


export function ExpenseFormModal({ open, initial, categories, onClose, onSaved }: Props) {
  const isEdit = !!initial;
  const qc = useQueryClient();
  const [date, setDate] = useState(todayIso());
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [secondaryCategoryId, setSecondaryCategoryId] = useState<number | "">("");
  const [accrualId, setAccrualId] = useState<number | "">("");
  const [amountStr, setAmountStr] = useState("");
  const [supplier, setSupplier] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("bank_transfer");
  const [accountId, setAccountId] = useState<number | "">("");
  const [objectId, setObjectId] = useState<number | "">("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** Файл, выбранный пользователем при создании. Загружается после POST /expenses. */
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const accountsQ = useQuery({
    queryKey: ["finance-accounts"],
    queryFn: () => fetchPaymentAccounts(),
    enabled: open,
  });
  const objectsQ = useQuery({
    queryKey: ["finance-objects-all"],
    queryFn: () => fetchObjects(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPendingFile(null);
    if (initial) {
      setDate(initial.date);
      setCategoryId(initial.category_id);
      setSecondaryCategoryId(initial.secondary_category_id ?? "");
      setAccrualId(initial.accrual_id ?? "");
      setAmountStr(String(initial.amount));
      setSupplier(initial.supplier || "");
      setPaymentMethod(initial.payment_method);
      setAccountId(initial.account_id ?? "");
      setObjectId(initial.object_id ?? "");
      setDescription(initial.description || "");
    } else {
      setDate(todayIso());
      setCategoryId(""); setSecondaryCategoryId(""); setAccrualId(""); setAmountStr(""); setSupplier("");
      setPaymentMethod("bank_transfer"); setAccountId("");
      setObjectId(""); setDescription("");
    }
  }, [open, initial]);

  const mut = useMutation({
    mutationFn: async () => {
      const amount = parseFloat(amountStr.replace(",", "."));
      if (!date) throw new Error("Укажите дату");
      if (!categoryId) throw new Error("Выберите категорию");
      if (!isFinite(amount) || amount <= 0) throw new Error("Сумма должна быть больше нуля");
      const payload: ExpensePayload = {
        date,
        category_id: Number(categoryId),
        secondary_category_id: secondaryCategoryId === "" ? null : Number(secondaryCategoryId),
        accrual_id: accrualId === "" ? null : Number(accrualId),
        amount,
        supplier: supplier.trim() || null,
        payment_method: paymentMethod,
        account_id: accountId === "" ? null : Number(accountId),
        object_id: objectId === "" ? null : Number(objectId),
        region: null,
        description: description.trim() || null,
        note: null,
      };
      if (isEdit) {
        return updateExpense(initial!.id, payload);
      }
      // Создание → POST expense, затем сразу upload файла (если выбран)
      const created = await createExpense(payload);
      if (pendingFile && created?.id) {
        try {
          await uploadExpenseAttachment(created.id, pendingFile);
        } catch (uploadErr) {
          // Expense создан, но файл не загрузился — не критично, юзер сможет
          // прикрепить через edit. Показываем warning отдельно.
          console.warn("attachment upload failed after create:", uploadErr);
          throw new Error(`Расход создан (id=${created.id}), но файл не загрузился: ${(uploadErr as Error).message}. Открой расход заново и прикрепи.`);
        }
      }
      return created;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-overview"] });
      void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      onSaved();
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail || `HTTP ${e.status}`)
        : (e as Error).message;
      setError(msg);
    },
  });

  // Опциональные секции (раскрываются по кнопке) — supplier пустой, accrual не привязан
  const [showSupplier, setShowSupplier] = useState(false);
  const [showAccrual, setShowAccrual] = useState(false);
  const [showSecondary, setShowSecondary] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  useEffect(() => {
    if (!open) return;
    setShowSupplier(!!initial?.supplier);
    setShowAccrual(!!initial?.accrual_id);
    setShowSecondary(!!initial?.secondary_category_id);
    setShowAccount(initial?.account_id != null);
  }, [open, initial]);

  if (!open) return null;
  const accounts = accountsQ.data?.items || [];
  const objects = objectsQ.data || [];

  // 7 главных категорий — по одной на kind, name == KIND_LABELS[kind].
  // Подкатегории в БД остаются (для старых расходов), но в форме их не показываем.
  const kindsOrder: CategoryKind[] = ["fot", "materials", "outsource", "taxes", "marketing", "sales", "other"];
  const mainByKind: Record<string, ExpenseCategoryRow | undefined> = {};
  for (const c of categories) {
    if (c.archived) continue;
    if (c.name === KIND_LABELS[c.kind as CategoryKind] && !mainByKind[c.kind]) {
      mainByKind[c.kind] = c;
    }
  }
  // Если редактируем старый расход с подкатегорией, не входящей в 7 главных —
  // подмешиваем её отдельной опцией, чтобы select показал текущее значение.
  const initialPrimary = isEdit ? categories.find(c => c.id === initial!.category_id) : null;
  const initialSecondary = isEdit && initial?.secondary_category_id
    ? categories.find(c => c.id === initial.secondary_category_id) : null;
  const isLegacy = (c: ExpenseCategoryRow | null | undefined) =>
    !!c && c.name !== KIND_LABELS[c.kind as CategoryKind];

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "grid", placeItems: "center", zIndex: 100, padding: 16 }}
    >
      <div className="card" style={{ maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24, position: "relative" }}>
        <button
          type="button" onClick={onClose} aria-label="Закрыть"
          style={{ position: "absolute", top: 12, right: 12, border: "none", background: "transparent", color: "var(--mute)", cursor: "pointer", fontSize: 20, padding: 4 }}
        >×</button>

        <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, marginBottom: 20, color: "var(--ink)" }}>
          {isEdit ? "Редактировать расход" : "Новый расход"}
        </h3>

        <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="row row-2">
            <label style={fieldStyle}>
              Дата
              <input type="date" className="input" required value={date}
                     onChange={e => setDate(e.target.value)} max={todayIso()}/>
            </label>
            <label style={fieldStyle}>
              Сумма, ₽
              <input type="text" inputMode="decimal" className="input" required value={amountStr}
                     onChange={e => setAmountStr(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="0"/>
            </label>
          </div>

          <label style={fieldStyle}>
            Категория
            <select className="input" required
                    value={categoryId === "" ? "" : String(categoryId)}
                    onChange={e => setCategoryId(e.target.value ? parseInt(e.target.value, 10) : "")}>
              <option value="">— выбрать —</option>
              {isLegacy(initialPrimary) && (
                <option value={initialPrimary!.id}>
                  ⚠ {KIND_LABELS[initialPrimary!.kind as CategoryKind]}: {initialPrimary!.name} (старая)
                </option>
              )}
              {kindsOrder.map(kind => mainByKind[kind] ? (
                <option key={kind} value={mainByKind[kind]!.id}>{KIND_LABELS[kind]}</option>
              ) : null)}
            </select>
          </label>

          {showSecondary ? (
            <label style={fieldStyle}>
              Дополнительная категория (тег, необязательно)
              <select className="input" autoFocus
                      value={secondaryCategoryId === "" ? "" : String(secondaryCategoryId)}
                      onChange={e => setSecondaryCategoryId(e.target.value ? parseInt(e.target.value, 10) : "")}>
                <option value="">— нет —</option>
                {isLegacy(initialSecondary) && (
                  <option value={initialSecondary!.id}>
                    ⚠ {KIND_LABELS[initialSecondary!.kind as CategoryKind]}: {initialSecondary!.name} (старая)
                  </option>
                )}
                {kindsOrder.map(kind => mainByKind[kind] ? (
                  <option key={kind} value={mainByKind[kind]!.id}
                          disabled={mainByKind[kind]!.id === categoryId}>{KIND_LABELS[kind]}</option>
                ) : null)}
              </select>
              <span style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
                Например: ФОТ маркетолога — основная «ФОТ», дополнительная «Маркетинг».
                На итоги не влияет, используется для фильтрации.
              </span>
            </label>
          ) : (
            <button type="button" className="btn btn-sm" style={{ alignSelf: "flex-start" }}
                    onClick={() => setShowSecondary(true)}>
              + Дополнительная категория (тег)
            </button>
          )}

          <label style={fieldStyle}>
            Способ оплаты
            <select className="input" value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value as PaymentMethod)}>
              {(Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map(m => (
                <option key={m} value={m}>{PAYMENT_LABELS[m]}</option>
              ))}
            </select>
          </label>

          {showAccount ? (
            <label style={fieldStyle}>
              Счёт / касса
              <select className="input" autoFocus
                      value={accountId === "" ? "" : String(accountId)}
                      onChange={e => setAccountId(e.target.value ? parseInt(e.target.value, 10) : "")}>
                <option value="">— не указан —</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}{a.bank_label ? ` (${a.bank_label})` : ""}</option>
                ))}
              </select>
            </label>
          ) : (
            <button type="button" className="btn btn-sm" style={{ alignSelf: "flex-start" }}
                    onClick={() => setShowAccount(true)}>
              + Счёт / касса
            </button>
          )}

          {showSupplier ? (
            <label style={fieldStyle}>
              Поставщик
              <input type="text" className="input" value={supplier} autoFocus
                     onChange={e => setSupplier(e.target.value)} placeholder="ИП Иванов"/>
            </label>
          ) : (
            <button type="button" className="btn btn-sm" style={{ alignSelf: "flex-start" }}
                    onClick={() => setShowSupplier(true)}>
              + Поставщик
            </button>
          )}

          <label style={fieldStyle}>
            Объект (опционально, для unit-экономики)
            <ObjectPicker
              value={objectId}
              objects={objects}
              onChange={setObjectId}
            />
          </label>

          {showAccrual ? (
            <label style={fieldStyle}>
              Привязать к ведомости начисления ЗП
              <AccrualPicker value={accrualId} onChange={setAccrualId}/>
              <span style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
                Расход появится в детали ведомости и автоматически зачтётся как «выплачено».
              </span>
            </label>
          ) : (
            <button type="button" className="btn btn-sm" style={{ alignSelf: "flex-start" }}
                    onClick={() => setShowAccrual(true)}>
              + Привязать к ведомости ЗП
            </button>
          )}

          <label style={fieldStyle}>
            Примечание
            <input type="text" className="input" value={description}
                   onChange={e => setDescription(e.target.value)}
                   placeholder="Закупка кирпича"/>
          </label>

          {/* Attachment в режиме создания — файл держим в state, грузим после POST /expenses.
              НЕ обязателен — расход без чека будет помечен в журнале «⚠ без чека». */}
          {!isEdit && (
            <PendingAttachmentInput file={pendingFile} onChange={setPendingFile} />
          )}

          {error && (
            <div className="alert alert-red"><div className="alert-body"><div>{error}</div></div></div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
              {mut.isPending ? "Сохранение…" : (isEdit ? "Сохранить" : "Добавить")}
            </button>
          </div>
        </form>

        {/* В режиме редактирования attachment живёт отдельно — загружается сразу. */}
        {isEdit && initial && (
          <AttachmentSection
            expenseId={initial.id}
            initialPath={initial.attachment_path || null}
          />
        )}
      </div>
    </div>
  );
}


/** Инпут выбора файла для НОВОГО expense. Сам файл не грузится — держится в
 *  состоянии родителя, который после POST /expenses делает upload. */
function PendingAttachmentInput({ file, onChange }: {
  file: File | null;
  onChange: (f: File | null) => void;
}) {
  return (
    <div style={{
      padding: "12px 14px",
      background: file ? "var(--green-soft)" : "var(--paper-2)",
      borderRadius: 8,
      border: `1px dashed ${file ? "rgba(61,122,61,0.4)" : "var(--line)"}`,
    }}>
      <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="paperclip" size={12}/> Чек / документ <span style={{ color: "var(--mute-2)", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(можно прикрепить позже)</span>
      </div>
      {file ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="file" size={13}/>{file.name}</span>
          <span style={{ fontSize: 11, color: "var(--mute)" }}>
            ({(file.size / 1024).toFixed(0)} KB)
          </span>
          <span style={{ flex: 1 }} />
          <label className="chip" style={{ cursor: "pointer" }}>
            Заменить
            <input
              type="file" hidden
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={e => { const f = e.target.files?.[0]; if (f) onChange(f); }}
            />
          </label>
          <button type="button" className="chip" onClick={() => onChange(null)}
                  style={{ color: "var(--brick)" }}>Убрать</button>
        </div>
      ) : (
        <label className="chip" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon name="paperclip" size={12}/> Прикрепить файл
          <input
            type="file" hidden
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={e => { const f = e.target.files?.[0]; if (f) onChange(f); }}
          />
        </label>
      )}
      <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 6 }}>
        До 25 MB. JPG / PNG / HEIC / PDF / DOC / XLS / CSV. Расходы без чека будут помечены в журнале.
      </div>
    </div>
  );
}


function AttachmentSection({ expenseId, initialPath }: {
  expenseId: number; initialPath: string | null;
}) {
  const qc = useQueryClient();
  const [path, setPath] = useState<string | null>(initialPath);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onPick = async (file: File) => {
    setErr(null);
    setUploading(true);
    try {
      const info = await uploadExpenseAttachment(expenseId, file);
      setPath(info.attachment_path);
      void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Удалить прикреплённый файл?")) return;
    setErr(null);
    try {
      await deleteExpenseAttachment(expenseId);
      setPath(null);
      void qc.invalidateQueries({ queryKey: ["finance-expenses"] });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const fileName = path?.split("/").pop() || "файл";

  return (
    <div style={{
      marginTop: 16, padding: "12px 14px",
      background: "var(--paper-2)", borderRadius: 8,
      border: "1px dashed var(--line)",
    }}>
      <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="paperclip" size={12}/> Чек / документ
      </div>
      {path ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <a href={expenseAttachmentUrl(expenseId)} target="_blank" rel="noopener noreferrer"
             className="card-link" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon name="file" size={13}/>{fileName}<Icon name="external" size={11}/>
          </a>
          <span style={{ flex: 1 }} />
          <label className="chip" style={{ cursor: "pointer" }}>
            Заменить
            <input
              type="file" hidden
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={e => { const f = e.target.files?.[0]; if (f) void onPick(f); }}
            />
          </label>
          <button type="button" className="chip" onClick={onDelete}
                  style={{ color: "var(--brick)" }}>Удалить</button>
        </div>
      ) : (
        <label className="chip" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
          {uploading ? "Загрузка…" : <><Icon name="paperclip" size={12}/>Прикрепить файл</>}
          <input
            type="file" hidden disabled={uploading}
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            onChange={e => { const f = e.target.files?.[0]; if (f) void onPick(f); }}
          />
        </label>
      )}
      {err && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--brick)" }}>
          Ошибка: {err}
        </div>
      )}
      <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 6 }}>
        До 25 MB. Поддержка: JPG / PNG / HEIC / PDF / DOC / XLS / CSV
      </div>
    </div>
  );
}


const fieldStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)",
};


function AccrualPicker({ value, onChange }: { value: number | ""; onChange: (v: number | "") => void }) {
  const q = useQuery({
    queryKey: ["payroll-accruals-all"],
    queryFn: () => fetchAccruals({}),
    staleTime: 60_000,
  });
  const items = q.data ?? [];
  return (
    <select className="input" value={value === "" ? "" : String(value)}
            onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : "")}>
      <option value="">— не привязан —</option>
      {items.map(a => (
        <option key={a.id} value={a.id}>
          #{a.id} · {a.user_name} · {a.ym} · {a.status === "approved" ? "утв" : a.status === "paid" ? "выпл" : "черн"} · к выплате {Math.round(a.balance).toLocaleString("ru-RU")} ₽
        </option>
      ))}
    </select>
  );
}


/** Поиск-комбобокс по объектам. Input + выпадашка с фильтрацией по client_name/address. */
function ObjectPicker({ value, objects, onChange }: {
  value: number | "";
  objects: { id: number; client_name: string; address?: string | null; period_label?: string | null }[];
  onChange: (v: number | "") => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Закрытие по клику вне
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = value !== "" ? objects.find(o => o.id === value) : null;

  const norm = (s: string) => s.toLowerCase().replace(/ё/g, "е");
  const q = norm(query.trim());
  const filtered = !q
    ? objects.slice(0, 50)
    : objects.filter(o => {
        const hay = norm(`${o.client_name || ""} ${o.address || ""} ${o.period_label || ""}`);
        return hay.includes(q);
      }).slice(0, 50);

  useEffect(() => { setHoverIdx(0); }, [query, open]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 8px",
        border: "1px solid var(--line)",
        background: "var(--card)",
        borderRadius: 6,
        cursor: "text",
      }} onClick={() => { setOpen(true); }}>
        {selected ? (
          <>
            <span style={{ flex: 1, fontSize: 13 }}>
              <strong>{selected.client_name}</strong>
              {selected.period_label && <span style={{ color: "var(--mute)" }}> · {selected.period_label}</span>}
            </span>
            <button type="button"
                    onClick={e => { e.stopPropagation(); onChange(""); setQuery(""); setOpen(false); }}
                    style={{ background: "none", border: "none", color: "var(--mute)", cursor: "pointer", padding: "0 4px" }}
                    title="Очистить"><Icon name="x" size={12}/></button>
          </>
        ) : (
          <input
            type="text" value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx(i => Math.min(filtered.length - 1, i + 1)); setOpen(true); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setHoverIdx(i => Math.max(0, i - 1)); }
              else if (e.key === "Enter") {
                e.preventDefault();
                const o = filtered[hoverIdx];
                if (o) { onChange(o.id); setQuery(""); setOpen(false); }
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            placeholder="поиск по клиенту, адресу или периоду…"
            style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13 }}
          />
        )}
      </div>
      {open && !selected && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "var(--card)", border: "1px solid var(--line)",
          borderRadius: 6, boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          zIndex: 30, maxHeight: 280, overflowY: "auto",
        }}>
          <div
            onMouseDown={() => { onChange(""); setOpen(false); }}
            style={{
              padding: "8px 10px", fontSize: 12, color: "var(--mute)",
              cursor: "pointer", borderBottom: "1px solid var(--line-soft)",
              background: hoverIdx === -1 ? "var(--paper-2)" : undefined,
            }}>
            — не привязан —
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--mute)" }}>
              Ничего не найдено по «{query}»
            </div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o.id}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseDown={() => { onChange(o.id); setQuery(""); setOpen(false); }}
                style={{
                  padding: "7px 10px",
                  cursor: "pointer",
                  background: i === hoverIdx ? "var(--paper-2)" : undefined,
                  borderBottom: "1px solid var(--line-soft)",
                  fontSize: 13,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {o.client_name}
                  {o.period_label && <span style={{ color: "var(--mute)", fontWeight: 400 }}> · {o.period_label}</span>}
                </div>
                {o.address && (
                  <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 1 }}>{o.address}</div>
                )}
              </div>
            ))
          )}
          <div style={{ padding: "6px 10px", fontSize: 10.5, color: "var(--mute)", background: "var(--paper-2)", textAlign: "center" }}>
            {filtered.length === 0 ? "0" : `${filtered.length}${filtered.length === 50 && q.length === 0 ? " (показано первых 50)" : ""}`} из {objects.length}
          </div>
        </div>
      )}
    </div>
  );
}
